/**
 * A view's face — title, one-sentence caption, and the chart itself — with everything else
 * (a template's own subtitle/lens note, callouts, the basis line) reachable through a
 * click-to-open dialog. Shared by the pin-board card and the chat views rail, so both surfaces
 * present a view identically: the claim inline, the reference material behind one click.
 *
 * The click target and a drag gesture (the pin board's grid) share the same surface: a genuine
 * click (pointer barely moved between down and up, or a keyboard activation) opens the dialog; a
 * drag does not, so dragging the chart to reposition a pinned card never also pops the dialog
 * open. Harmless where there's no drag to guard against (the chat rail) — a real click's
 * pointerdown-to-click displacement is essentially zero anyway.
 */

import { type KeyboardEvent, type MouseEvent, type PointerEvent, type ReactNode, useRef, useState } from "react"; // prettier-ignore

import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";
import type { ViewRecord } from "@/lib/api";

import { SpecView, type ViewSize } from "./spec-view";

/** Beyond this many CSS px between pointerdown and the click, treat it as a drag — not a click
 *  meant to open the dialog. Keyboard-activated clicks carry no pointerdown at all, so they
 *  always open it. */
const DRAG_THRESHOLD_PX = 5;

interface ViewCardProps {
  spec: ViewRecord["spec"];
  id?: string;
  /** Face size — the popup always renders at `"expanded"` regardless. */
  size?: ViewSize;
  /** Extra content under the popup's chart, e.g. the dashboard's "from conversation" link. */
  footer?: ReactNode;
  /** Extra classes for the clickable face wrapper, e.g. to make room for a sibling control. */
  className?: string;
}

export function ViewCard({ spec, id, size = "card", footer, className }: ViewCardProps) {
  const [open, setOpen] = useState(false);
  const downRef = useRef<{ x: number; y: number } | null>(null);

  function handlePointerDown(e: PointerEvent) {
    downRef.current = { x: e.clientX, y: e.clientY };
  }

  function handleClick(e: MouseEvent) {
    const down = downRef.current;
    downRef.current = null;
    if (down) {
      const dx = e.clientX - down.x;
      const dy = e.clientY - down.y;
      if (Math.hypot(dx, dy) > DRAG_THRESHOLD_PX) {
        return;
      }
    }
    setOpen(true);
  }

  function handleKeyDown(e: KeyboardEvent) {
    if (e.key === "Enter" || e.key === " ") {
      e.preventDefault();
      setOpen(true);
    }
  }

  return (
    <>
      <div
        role="button"
        tabIndex={0}
        aria-label={`View details: ${spec.title}`}
        onPointerDown={handlePointerDown}
        onClick={handleClick}
        onKeyDown={handleKeyDown}
        className={className ?? "min-h-0 min-w-0 flex-1 cursor-pointer"}
      >
        <SpecView spec={spec} size={size} id={id} chrome="bare" />
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-2xl">
          <DialogTitle className="sr-only">{spec.title}</DialogTitle>
          <div className="max-h-[70vh] overflow-y-auto pr-1">
            <SpecView spec={spec} size="expanded" id={id} chrome="full" />
          </div>
          {footer}
        </DialogContent>
      </Dialog>
    </>
  );
}
